<script setup lang="ts">
import AppIcon from '@/shared/ui/AppIcon/index';
import { FeedbackModal, prefetchFeedbackModal } from '@/widgets/FeedbackModal/index';
import { useFeedbackFlow } from '@/widgets/FeedbackModal/composables/useFeedbackFlow';
import { useOpenedModals } from '@/shared/composables/useOpenedModals';
import { useUserStore } from '@/entities/user/store';
import { FEEDBACKS_FETCH_ERROR } from '@/entities/feedback/lib/feedbackMessages';
import FeedbackCard from '@/pages/lawyer/ui/sections/FeedbacksSection/FeedbackCard.vue';
import FeedbacksEmptyPlaceholder from '@/pages/lawyer/ui/sections/FeedbacksSection/FeedbacksEmptyPlaceholder.vue';
import { modifyFeedback } from '@/entities/feedback/lib/modifyFeedback';
import { scroll } from '@/shared/utils/scrollBehavior';
import { LAWYER_INFO } from '@/pages/lawyer/model/keys';
import type {
  IFeedback,
  IFeedbacksResponse,
} from '@/entities/feedback/model/types/feedback';
import type { FeedbacksPage } from '@/pages/lawyer/model/types/feedbacksSection';
import { useErrorHandler, logError, normalizeError } from '@/shared/lib/errors';

const PER_PAGE = 2;

const isMobile: boolean = useIsMobile();
const lawyerInfo = inject(LAWYER_INFO);
const userStore = useUserStore();
const { openFeedback } = useFeedbackFlow();
const { openedModals } = useOpenedModals(['feedback'], prefetchFeedbackModal);
const { $feedbackService } = useNuxtApp();
const { handleError } = useErrorHandler();

const sectionRef: Ref<HTMLElement | null> = ref(null);
const currentPage: Ref<number> = ref(1);
const isLoading: Ref<boolean> = ref(false);
const extraFeedbacks: Ref<IFeedback[]> = ref([]);

const rateCount: ComputedRef<number> = computed(
  () => lawyerInfo?.value?.feedbackCount ?? 0,
);
const rateValue: ComputedRef<number> = computed(
  () => lawyerInfo?.value?.feedbackRating ?? 0,
);

const extractTotal = (response: IFeedbacksResponse): number => response.meta?.total ?? 0;

const emptyPage = (): FeedbacksPage => ({ items: [], total: 0 });

const fetchPage = (page: number) =>
  $feedbackService.getAllFeedbacks({
    target_user_id: lawyerInfo?.value?.user_id ?? 0,
    page,
    per_page: PER_PAGE,
  });

const { data: firstPage } = useAsyncData<FeedbacksPage>(
  () => `feedbacks:${lawyerInfo?.value?.user_id ?? 'none'}`,
  async () => {
    if (!lawyerInfo?.value?.user_id) return emptyPage();
    try {
      const response = await fetchPage(1);
      return {
        items: response.data.map(modifyFeedback),
        total: extractTotal(response),
      };
    } catch (error) {
      logError(normalizeError(error), { action: 'lawyer-feedbacks' });
      return emptyPage();
    }
  },
  {
    default: emptyPage,
    watch: [() => lawyerInfo?.value?.user_id],
    getCachedData: (key, nuxtApp, ctx) =>
      ctx.cause === 'initial' ? nuxtApp.payload.data[key] : undefined,
  },
);

const feedbackList: ComputedRef<IFeedback[]> = computed(() => [
  ...(firstPage.value?.items ?? []),
  ...extraFeedbacks.value,
]);

const totalItems: ComputedRef<number> = computed(() => firstPage.value?.total ?? 0);

const isShowLoadMore: ComputedRef<boolean> = computed(
  () => feedbackList.value.length < totalItems.value,
);
const moreButtonText: ComputedRef<string> = computed(() =>
  isShowLoadMore.value ? 'Показать ещё' : 'Свернуть',
);

const canWriteFeedback: ComputedRef<boolean> = computed(
  () => !userStore.isAuth || userStore.isRoleTypeUser,
);

const isShowWriteFeedback: ComputedRef<boolean> = computed(
  () => feedbackList.value.length > 0 && canWriteFeedback.value,
);

const openFeedbackModal = (): void => {
  openFeedback({ lawyerInfo: lawyerInfo?.value ?? null });
};

const onFeedbackSubmitted = async (): Promise<void> => {
  extraFeedbacks.value = [];
  currentPage.value = 1;
  await refreshNuxtData(`feedbacks:${lawyerInfo?.value?.user_id ?? 'none'}`);
};

const loadMore = async (): Promise<void> => {
  if (!isShowLoadMore.value) {
    extraFeedbacks.value = [];
    currentPage.value = 1;
    scroll(sectionRef.value, 100);
    return;
  }

  isLoading.value = true;
  try {
    currentPage.value += 1;
    const response = await fetchPage(currentPage.value);
    extraFeedbacks.value = [
      ...extraFeedbacks.value,
      ...response.data.map(modifyFeedback),
    ];
  } catch (error) {
    handleError(error, { context: { action: FEEDBACKS_FETCH_ERROR } });
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <section
    id="feedbacks"
    ref="sectionRef"
    :class="['lawyer-feedbacks-section', { _mobile: isMobile }]"
  >
    <div class="lawyer-feedbacks-section__header">
      <h2 class="lawyer-feedbacks-section__title">Отзывы</h2>
      <div class="lawyer-feedbacks-section__count">
        ({{ rateCount }})
        <span v-if="rateCount" class="lawyer-feedbacks-section__rating">
          <AppIcon class="lawyer-feedbacks-section__icon" name="Star" />
          <span class="lawyer-feedbacks-section__number">{{ rateValue.toFixed(1) }}</span>
        </span>
      </div>
    </div>

    <div class="lawyer-feedbacks-section__body">
      <ul v-if="feedbackList.length" class="lawyer-feedbacks-section__list">
        <li
          v-for="feedback in feedbackList"
          :key="feedback.id"
          class="lawyer-feedbacks-section__list-item"
        >
          <FeedbackCard :feedback="feedback" />
        </li>
      </ul>
      <div v-else-if="!isLoading" class="lawyer-feedbacks-section__empty">
        <FeedbacksEmptyPlaceholder
          :is-show-button="canWriteFeedback"
          @click="openFeedbackModal"
        />
      </div>

      <div
        v-if="totalItems > PER_PAGE || feedbackList.length"
        class="lawyer-feedbacks-section__actions"
      >
        <button
          v-if="totalItems > PER_PAGE"
          :class="['lawyer-feedbacks-section__more', { _revert: !isShowLoadMore }]"
          type="button"
          :aria-label="moreButtonText"
          @click.prevent="loadMore"
        >
          {{ moreButtonText }}
          <AppIcon name="ArrowDownSmall" />
        </button>
        <div v-if="isShowWriteFeedback" class="lawyer-feedbacks-section__feedback">
          <AppButton
            class="lawyer-feedbacks-section__review"
            is-big
            button-text="Написать отзыв"
            @click="openFeedbackModal"
          />
        </div>
      </div>
    </div>
    <FeedbackModal v-if="openedModals.feedback" @submitted="onFeedbackSubmitted" />
  </section>
</template>

<style lang="less">
.lawyer-feedbacks-section {
  &__header {
    display: flex;
    align-items: center;
    margin-bottom: @size-x5;
  }

  &__title {
    font-weight: 600;
    font-size: @size-x6;
    line-height: 25px;
    color: @color-elephant;
    margin: 0 18px 0 0;
  }

  &__count {
    display: inline-flex;
    align-items: center;
    gap: @size-x2;
    font-size: @size-x4;
    line-height: 22px;
    font-weight: 400;
    color: @color-dove-gray;
  }

  &__rating {
    display: inline-flex;
    align-items: center;
    gap: @main-size;
    color: @color-persimmon;
    font-weight: 600;
  }

  &__icon {
    width: @size-x4;
    height: @size-x4;

    path {
      fill: @color-mustard;
      stroke: @color-mustard;
    }
  }

  &__body {
    .box-shadow();

    background: #fff;
    border-radius: @main-size;
    padding: 30px;
  }

  &__list-item:not(:last-child) {
    margin-bottom: 30px;
  }

  &__empty {
    font-weight: 500;
    text-align: center;
    padding: 60px;
  }

  &__actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: @size-x8;
  }

  &__more {
    display: inline-flex;
    align-items: center;
    gap: @size-x2;
    font-size: 14px;
    line-height: 19px;
    font-weight: 700;
    color: @color-elephant;

    svg path {
      stroke: @color-persimmon;
    }

    &:hover {
      color: @color-persimmon;
    }

    &._revert svg {
      transform: rotateZ(180deg);
    }
  }

  &__feedback {
    margin-left: auto;
  }

  &._mobile &__header {
    margin-bottom: @size-x4;
  }

  &._mobile &__body {
    padding: 30px @size-x4;
  }

  &._mobile &__empty {
    padding: 0;
  }

  &._mobile &__actions {
    display: block;
  }

  &._mobile &__more {
    margin-bottom: @size-x8;
  }

  &._mobile &__feedback {
    display: flex;
  }

  &._mobile &__review {
    margin-left: auto;
  }
}
</style>
